import Link from "next/link";

import CodeBlock from "@/components/docs/CodeBlock";
import { DocCode, DocList, DocP, DocSection, DocTable } from "@/components/docs/Prose";
import { API, OPENAPI_PATH, getGroup, groupTitle } from "@/lib/docs/api";
import { docPath } from "@/lib/docs/content";
import { absoluteUrl } from "@/lib/seo/schema";

const LINK = "font-medium text-[#335CFF] underline decoration-[#335CFF]/30 underline-offset-[3px] hover:decoration-[#335CFF]";

const OpenApiGuide = () => {
  const specUrl = absoluteUrl(OPENAPI_PATH);
  const sample = API.endpoints.find((endpoint) => endpoint.id === "export-project");
  const tier = sample ? API.rateTiers[sample.rateTier] : null;
  const sampleGroup = sample ? getGroup(sample.group) : undefined;

  const operationExcerpt = sample
    ? JSON.stringify(
        {
          [sample.path]: {
            [sample.method.toLowerCase()]: {
              operationId: sample.id,
              summary: sample.title,
              tags: [sampleGroup ? groupTitle(sampleGroup) : sample.group],
              "x-credits": sample.credits,
              "x-rate-limit": {
                tier: sample.rateTier,
                requests_per_second: tier?.perSecond,
                requests_per_minute: tier?.perMinute,
              },
            },
          },
        },
        null,
        2,
      )
    : "";

  return (
    <div className="flex flex-col gap-12">
      <DocSection id="download" title="Download the specification">
        <DocP>
          The document is generated from the same endpoint registry that renders these pages and
          the API console in the Crayo app, so it always matches what is documented here. It is
          served as plain JSON.
        </DocP>
        <CodeBlock code={`curl -o crayo-openapi.json "${specUrl}"`} label="curl" />
        <DocList
          items={[
            <span key="version">
              Format: OpenAPI <DocCode>3.1.0</DocCode>, title <DocCode>Crayo API</DocCode>, server{" "}
              <DocCode>{API.baseUrl}</DocCode>.
            </span>,
            <span key="auth">
              Security: one <DocCode>bearerAuth</DocCode> HTTP bearer scheme applied to every
              operation. Supply a key that starts with <DocCode>{API.apiKeyPrefix}</DocCode>.
            </span>,
            <span key="count">
              Coverage: {API.endpoints.length} operations across {API.groups.length} tags, one tag
              per endpoint group in these docs.
            </span>,
          ]}
        />
      </DocSection>

      <DocSection id="structure" title="What each operation contains">
        <DocP>
          Operations are keyed by path and lower-case method. Each one carries the endpoint id as{" "}
          <DocCode>operationId</DocCode>, the description and notes from these docs, path and query
          parameters with enums and defaults, a JSON request body schema with an example where the
          endpoint takes a body, and a 200 response schema inferred from the example response.
        </DocP>
        <CodeBlock code={operationExcerpt} label="excerpt" />
        <DocList
          items={[
            <span key="credits">
              <DocCode>x-credits</DocCode> is the plain-language cost string shown on every endpoint
              page.
            </span>,
            <span key="rate">
              <DocCode>x-rate-limit</DocCode> names the tier and its requests per second and per
              minute, the same numbers as the{" "}
              <Link href={`${docPath("getting-started")}#rate-limits`} prefetch={false} className={LINK}>
                rate limit tables
              </Link>
              .
            </span>,
            <span key="errors">
              Error responses are listed per status with example bodies keyed by code. The shared
              401, 429 and 500 responses are present on every operation.
            </span>,
          ]}
        />
      </DocSection>

      <DocSection id="schemas" title="Shared schemas">
        <DocP>
          <DocCode>components.schemas</DocCode> defines the <DocCode>Error</DocCode> envelope and the
          objects that View Tracker responses and webhooks reuse everywhere they appear. A field we
          do not have is null, never omitted.
        </DocP>
        <DocTable
          headers={["Schema", "Description"]}
          rows={API.sharedObjects.map((object) => [
            <span key="s" className="font-mono text-[12px]">
              {object.schema}
            </span>,
            object.description,
          ])}
        />
      </DocSection>

      <DocSection id="tags" title="Tags">
        <DocTable
          headers={["Tag", "Endpoints", "Reference"]}
          rows={API.groups.map((group) => [
            groupTitle(group),
            String(group.endpointIds.length),
            <Link key="l" href={docPath(group.id)} prefetch={false} className={LINK}>
              {docPath(group.id)}
            </Link>,
          ])}
        />
      </DocSection>
    </div>
  );
};

export default OpenApiGuide;
